import { isNil } from 'lodash';
import { Component, useCallback, useContext } from 'react';
import {
  useCardSelectionActions,
  useCardSelectionState,
} from '@shared/api/providers/cardSelection';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { useDuplicateCard } from '@shared/scenes/cards/form/hooks';
import { MBActionSheet } from '@shared/util/actionSheet';
import { MBAnalytics } from '@shared/util/analytics';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { PrintContext } from '@shared/util/print/PrintContext';
import { useBulkEditMenuItem } from './useBulkEditMenuItem';
import { useRemoveCardWithConfirmation } from './useRemoveCardWithConfirmation';

type CardOption =
  | 'bulkUpdate'
  | 'print'
  | 'selectMultiple'
  | 'edit'
  | 'delete'
  | 'share'
  | 'download'
  | 'cardHistory'
  | 'duplicate';

export type HiddenCardOptions = CardOption[];

interface UseOpenCardOptionsMenuOptions {
  hiddenCardOptions?: HiddenCardOptions;
  showEdit?: boolean;
  onOpen?(): void;
  onClose?(): void;
  onRemoved?(): void;
  onShare?(): void;
  onDownload?(): void;
}

export function useOpenCardOptionsMenu(
  card: MBAPI.Card,
  options: UseOpenCardOptionsMenuOptions,
): (anchor: Component) => void {
  const { hiddenCardOptions, onOpen, onClose, onRemoved, onShare, onDownload } = options;
  const channel = useContext(CardChannelContext);
  const duplicateCard = useDuplicateCard();
  const { navigate, replace } = useNavigator();
  const removeCardWithConfirmation = useRemoveCardWithConfirmation(card, onRemoved);
  const { canCreateCard, canUpdateCard, canDeleteCard } = useContext(ChannelPermissionsContext);
  const isDesktop = useIsDesktop();

  const printContext = useContext(PrintContext);
  const renderContext = useContext(CardRenderContext);

  const selectionState = useCardSelectionState();
  const { setActive: setSelectionActive, addCard } = useCardSelectionActions();
  const isCollectionView = renderContext && !['form', 'detail'].includes(renderContext);
  const cardSelectionEnabled = selectionState && isCollectionView && isDesktop;
  const isLowCodeCard = isNil(card.workflowTemplateId);

  const recurringEntitiesScheduleId = card.fields?.entityBatch;
  const showBulkUpdateItem =
    !isNil(recurringEntitiesScheduleId) &&
    !isNil(channel?.workflowTemplateId) &&
    !hiddenCardOptions?.includes('bulkUpdate');

  const cardId = card.id;
  const channelSlug = channel?.slug;
  const workflowTemplateId = channel?.workflowTemplateId;
  const isNoCodeWorkspace = !isNil(card.workflowTemplateId);

  const navigateToBulkUpdate = useBulkEditMenuItem({
    cardId,
    channelSlug,
    workflowTemplateId,
    recurringEntitiesScheduleId,
  });

  return useCallback(
    (anchor: Component) => {
      onOpen?.();
      return MBActionSheet.show({
        parent: anchor,
        webVerticalAlignment: 'below',
        webHorizontalAlignment: 'right',
        onClose,
        options: [
          {
            title: i18n('common.edit'),
            hidden:
              isNil(channelSlug) || !canUpdateCard.enabled || hiddenCardOptions?.includes('edit'),
            onPress: () => {
              navigate('CardUpdate', { cardId, channelSlug: channelSlug ?? '' });
            },
          },
          {
            title: isNoCodeWorkspace
              ? i18n('card.menu.actions.duplicateFrom')
              : i18n('card.menu.actions.duplicate'),
            hidden: !canCreateCard.enabled || hiddenCardOptions?.includes('duplicate'),
            onPress: () => {
              if (isNoCodeWorkspace) {
                replace('CardCreate', {
                  channelSlug: channelSlug ?? '',
                  fromEntity: cardId,
                });
              } else {
                duplicateCard(card.id);
              }
            },
          },
          {
            title: i18n('common.share'),
            hidden: hiddenCardOptions?.includes('share'),
            onPress: onShare,
          },
          {
            title: i18n('common.download'),
            hidden: isNil(onDownload) || isLowCodeCard || hiddenCardOptions?.includes('download'),
            onPress: onDownload,
          },
          {
            title: i18n('card.menu.actions.cardHistory'),
            hidden: isNil(channelSlug) || hiddenCardOptions?.includes('cardHistory'),
            onPress: () => {
              navigate('CardHistory', { cardId, channelSlug: channelSlug ?? '' });
            },
          },
          {
            title: i18n('card.bulkUpdate.menu.menuItem'),
            hidden: !showBulkUpdateItem || !canUpdateCard.enabled,
            onPress: () => navigateToBulkUpdate(anchor),
          },
          {
            title: i18n('card.menu.actions.print'),
            hidden: !printContext || hiddenCardOptions?.includes('print'),
            onPress: () => {
              MBAnalytics.track('PrintCard', { cardId: card.id, channelSlug: channel?.slug });
              printContext?.onPrint();
            },
          },
          {
            title: i18n('card.menu.actions.selectMultiple'),
            hidden: !cardSelectionEnabled || hiddenCardOptions?.includes('selectMultiple'),
            onPress: () => {
              addCard(card);
              setSelectionActive(true);
            },
          },
          {
            title: i18n('common.delete'),
            destructive: true,
            hidden: !canDeleteCard.enabled || hiddenCardOptions?.includes('delete'),
            onPress: () => removeCardWithConfirmation(anchor),
          },
        ],
      });
    },
    [
      onOpen,
      onClose,
      canUpdateCard.enabled,
      navigateToBulkUpdate,
      canCreateCard.enabled,
      onShare,
      onDownload,
      isLowCodeCard,
      printContext,
      cardSelectionEnabled,
      canDeleteCard.enabled,
      card,
      channel?.slug,
      addCard,
      setSelectionActive,
      removeCardWithConfirmation,
      showBulkUpdateItem,
      hiddenCardOptions,
      navigate,
      replace,
      cardId,
      channelSlug,
      duplicateCard,
      isNoCodeWorkspace,
    ],
  );
}
